import { useEffect, useState, useSyncExternalStore } from "react";

let eventBusInstance = null; //defining the variable for storing host event bus instance
let bridgeInstance = null;
let initPromise = null;

/**
 * Waits for the bridge to be initialized.
 * Listens for the host-dispatched `__HOST_BRIDGE_READY__` CustomEvent first
 * (zero delay when the host is already ready), and falls back to a polling
 * timeout of up to `timeoutMs` ms.
 */
const waitForBridgeReady = (bridge, timeoutMs = 1000) =>
  new Promise((resolve) => {
    if (bridge?.isInitialized?.()) return resolve();

    let settled = false;
    const settle = () => {
      if (settled) return;
      settled = true;
      globalThis.removeEventListener("__HOST_BRIDGE_READY__", onEvent);
      clearTimeout(timer);
      resolve();
    };

    const onEvent = () => settle();
    globalThis.addEventListener("__HOST_BRIDGE_READY__", onEvent, {
      once: true,
    });

    // Fallback: if the host never fires the event, resolve after timeout
    const timer = setTimeout(settle, timeoutMs);
  });

const initializeHostModules = async () => {
  if (initPromise) return initPromise;

  initPromise = (async () => {
    try {
      // Try globalThis first (already initialized)
      if (globalThis.__HOST_BRIDGE_INSTANCE__) {
        //checking HOST_BRIDGE_INSTANCE present in globalThis object
        bridgeInstance = globalThis.__HOST_BRIDGE_INSTANCE__; //then assign the HOST_BRIDGE_INSTANCE to bridgeInstance variable
        if (import.meta.env.DEV)
          console.log("[Remote] Found bridge on globalThis");
      } else {
        //if HOST_BRIDGE_INSTANCE is not available
        const bridgeModule = {};
        bridgeInstance = bridgeModule.hostBridge || bridgeModule.default; //assign the hostBridge object to the bridgeInstance
        if (import.meta.env.DEV) console.log("[Remote] Loaded bridge from MF");
      }

      // Wait for initialization — event-driven with timeout fallback
      await waitForBridgeReady(bridgeInstance);

      if (!bridgeInstance?.isInitialized?.()) {
        if (import.meta.env.DEV)
          console.warn("[Remote] Bridge not initialized after waiting");
      }

      // Load event bus
      if (globalThis.__EVENT_BUS__) {
        //checking EVENT_BUS object present in globalThis object
        eventBusInstance = globalThis.__EVENT_BUS__; //storing the event us instance to eventBusInstance variable
      } else {
        //EVENT_BUS object is not present in globalThis object
        const eventModule = {};
        eventBusInstance = eventModule.eventBus; //from the event module take eventBus object and store in eventBusInstance
      }

      if (import.meta.env.DEV)
        console.log("[Remote] Host modules initialized successfully");
      return { eventBus: eventBusInstance, bridge: bridgeInstance }; //return the eventBus and bridge instances
    } catch (error) {
      if (import.meta.env.DEV) console.error("[Remote] Init failed:", error);
      initPromise = null;
      throw error;
    }
  })();

  return initPromise; //return the initPromise function
};

export const useHostActions = () => {
  const [actions, setActions] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const init = async () => {
      try {
        const { bridge } = await initializeHostModules(); //loading the bridge instance from the host application
        setActions(bridge.getActions()); //setting the bridge instance getActions() method and storing all actions inside actions state
        setLoading(false); //setting the loading state as false
      } catch (err) {
        if (import.meta.env.DEV)
        console.error("[Remote] Error loading actions:", err);
        setLoading(false); //if error caught set loading state as false
      }
    };

    init(); //calling the init function
  }, []);
  return { ...actions, loading }; //it will return the actions and loading states
};


// new hooks for using the host state variable from the remote appliacation
// ========================== Host Selector for shared state ===========================
// ===================================================================================

export const useHostSelector = (selector) => {
  const bridge = globalThis.__HOST_BRIDGE_INSTANCE__;
  if (!bridge) {
    throw new Error("HostBridge not available");
  }

  return useSyncExternalStore(
    (listener) => bridge.subscribe(listener),
    () => selector(bridge.getRootState()),
    () => selector(bridge.getRootState()),
  );
};
